import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { format } from 'date-fns';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { NSUTPermitConstants } from '@ruby/configs/unit-train-permit.constants';
import { IUTShipment } from '@ruby/modules/unit-train/models/unit-train-permit.interface';

@Component({
  selector: 'ng-add-shipments-dialog',
  templateUrl: './add-shipments.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddShipmentsDialogComponent implements OnInit, OnDestroy {
  @Input() origins: Array<INSAutoCompleteOption> = [];
  @Input() shippers: Array<INSAutoCompleteOption> = [];
  @Input() trainTypes: Array<INSAutoCompleteOption> = NSUTPermitConstants.trainTypes;
  @Input() productClasses: Array<INSAutoCompleteOption> = [];
  @Output() getOrigins = new EventEmitter<{ commodity: string }>();
  @Output() getShippers = new EventEmitter<{ commodity: string; search?: string }>();
  @Output() getProductClasses = new EventEmitter<{ commodity: string }>();
  @Output() checkValidEquipment = new EventEmitter<{ equipment: string }>();
  form = new UntypedFormGroup({
    origin: new UntypedFormControl(undefined, Validators.required),
    shipper: new UntypedFormControl(undefined, Validators.required),
    requiredTrainType: new UntypedFormControl({ value: undefined, disabled: true }),
    noOfCars: new UntypedFormControl(undefined, [Validators.required, Validators.min(1), Validators.max(999)]),
    productClass: new UntypedFormControl(undefined),
    scheduleDeliveryDate: new UntypedFormControl(undefined),
    scheduleDeliveryTime: new UntypedFormControl({ value: undefined, disabled: true }),
    scheduleLoadDate: new UntypedFormControl(undefined, Validators.required),
    scheduleLoadTime: new UntypedFormControl({ value: undefined, disabled: true }),
    schLdDtLockDown: new UntypedFormControl({ disabled: true, value: true }),
    traceCar: new UntypedFormControl(undefined),
    needByDate: new UntypedFormControl({ value: undefined, disabled: true }),
    scheduleInterchangeDate: new UntypedFormControl({ value: undefined, disabled: true }),
    schIchDtLockDown: new UntypedFormControl({ disabled: true, value: true }),
    cducomments: new UntypedFormControl({ value: undefined, disabled: true })
  });
  quantity: number;
  current = 1;
  dialogTitle: string;
  nextButtonText: string;
  shipments: Array<IUTShipment> = [];
  commodity: string;
  hoursTimes: Array<INSAutoCompleteOption> = [];
  shipment?: IUTShipment;
  private subscriptionNotifier = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { shipment: IUTShipment; quantity: number; commodity: string },
    private dialogRef: MatDialogRef<AddShipmentsDialogComponent>
  ) {
    this.shipment = data.shipment;
    this.quantity = data.quantity;
    this.dialogTitle = this.getTitle();
    this.nextButtonText = this.getNextButtonText();
    this.commodity = data.commodity;

    this.setup(data.commodity);

    if (this.shipment) {
      this.setupEditShipment(this.shipment);
    }
  }

  ngOnInit(): void {
    this.getProductClasses.emit({ commodity: this.commodity });
    this.getOrigins.emit({ commodity: this.commodity });
    this.getShippers.emit({ commodity: this.commodity });
  }

  ngOnDestroy(): void {
    this.subscriptionNotifier.next();
    this.subscriptionNotifier.complete();
  }

  /**
   * Next
   *
   * @summary submit the shipments that were entered in the dialog form
   * @return void
   */
  next(): void {
    this.shipments.push(this.getData());
    this.form.reset();
    if (this.current === this.quantity) {
      this.dialogRef.close(this.shipments);
    } else {
      this.current++;
      this.dialogTitle = this.getTitle();
      this.nextButtonText = this.getNextButtonText();
    }
  }

  /**
   * Update
   *
   * @summary submit the shipment that was entered for edit
   * @return void
   */
  update(): void {
    const data = this.getData();
    this.dialogRef.close(data);
  }

  /**
   * Get Title
   *
   * @summary compute the title of the dialog
   * @return what the title of the dialog should be
   */
  getTitle(): string {
    if (this.shipment) {
      return 'Modify Shipment';
    } else if (this.quantity === 1) {
      return 'Add Shipment';
    }
    return `Add Shipments (${ this.current }/${ this.quantity })`;
  }

  /**
   * Get Next Button Text
   *
   * @summary compute the text of the next button
   * @return what the text of the next button should be
   */
  getNextButtonText(): string {
    if (this.quantity === this.current) {
      return 'Save';
    }
    return 'Next';
  }

  private getData(): IUTShipment {
    const data = this.form.getRawValue();
    return {
      ...this.shipment || {},
      ...data,
      isModified: true,
      originDesc: this.getValueDescription(this.origins, data.origin),
      shipperDesc: this.getValueDescription(this.shippers, data.shipper),
      scheduleDeliveryTime: this.getParsedTime(data.scheduleDeliveryTime),
      scheduleLoadTime: this.getParsedTime(data.scheduleLoadTime),
      originalLoadDate: data.scheduleLoadDate,
      originalLoadTime: this.getParsedTime(data.scheduleLoadTime)
    } as IUTShipment;
  }

  private getValueDescription(catalog: Array<INSAutoCompleteOption>, key: string): string | undefined {
    return catalog.find((value: INSAutoCompleteOption) => value.key === key)?.value;
  }

  private getParsedTime(time: string): Date {
    const currentDate: Date = new Date();
    currentDate.setHours(0, 0, 0, 0);
    if (time) {
      const [hours, minutes] = time.substr(0, time.length - 3).split(':').map(Number);
      const hoursVal = (time.includes('PM') && hours !== 12) ? hours + 12 : hours;
      currentDate.setHours(hoursVal, minutes);
    }
    return currentDate;
  }

  private getParsedDate(date: Date | number): Date {
    if (typeof date === 'number') {
      return new Date(date);
    }
    return date;
  }

  private getParsedTimeStr(date: Date | number | string): string {
    if (typeof date === 'number') {
      return format(new Date(date), 'hh:mm a');
    } else if (date instanceof Date) {
      return format(date, 'hh:mm a');
    }
    return date;
  }

  private setup(commodity: string): void {
    this.hoursTimes = NSUTPermitConstants.getTimesHours();

    this.form.controls.origin.valueChanges.pipe(
      takeUntil(this.subscriptionNotifier),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((origin: string) => {
      if (origin) {
        this.getShippers.emit({ commodity, search: origin });
      }
    });

    this.form.controls.requiredTrainType.valueChanges.pipe(
      takeUntil(this.subscriptionNotifier),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((value: string) => {
      if (value) {
        this.form.controls.noOfCars.setValue(value);
      }
    });

    this.form.controls.scheduleDeliveryDate.valueChanges.pipe(
      takeUntil(this.subscriptionNotifier),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((value: Date) => {
      if (value) {
        this.form.controls.scheduleDeliveryTime.enable();
      } else {
        this.form.controls.scheduleDeliveryTime.disable();
      }
    });

    this.form.controls.scheduleLoadDate.valueChanges.pipe(
      takeUntil(this.subscriptionNotifier),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((value: Date) => {
      if (value) {
        this.form.controls.scheduleLoadTime.enable();
      } else {
        this.form.controls.scheduleLoadTime.disable();
      }
    });

    this.setupControlsByCommodity(commodity);
  }

  private setupControlsByCommodity(commodity: string): void {
    switch (commodity) {
      case 'SS':
        this.form.controls.requiredTrainType.enable();
        break;
      case 'CO':
      case 'ET':
        this.form.controls.needByDate.enable();
        this.form.controls.scheduleInterchangeDate.enable();
        this.form.controls.schIchDtLockDown.enable();
        this.form.controls.cducomments.enable();
        break;
      case 'CL':
        this.form.controls.requiredTrainType.disable();
        this.form.controls.needByDate.disable();
        this.form.controls.scheduleInterchangeDate.disable();
        this.form.controls.schIchDtLockDown.disable();
        this.form.controls.cducomments.disable();
        break;
      default:
        this.form.controls.requiredTrainType.enable();
        this.form.controls.needByDate.enable();
        this.form.controls.scheduleInterchangeDate.enable();
        this.form.controls.schIchDtLockDown.enable();
        this.form.controls.cducomments.enable();
        break;
    }
    if (commodity === 'ET') {
      this.form.controls.scheduleDeliveryDate.setValidators([Validators.required]);
      this.form.controls.scheduleDeliveryDate.updateValueAndValidity();
    }
  }

  private setupEditShipment(shipment: IUTShipment): void {
    this.shipment = {
      ...shipment,
      origin: String(shipment.origin),
      shipper: String(shipment.shipper),
      scheduleDeliveryDate: this.getParsedDate(shipment.scheduleDeliveryDate),
      scheduleDeliveryTime: this.getParsedTimeStr(shipment.scheduleDeliveryTime),
      scheduleLoadDate: this.getParsedDate(shipment.scheduleLoadDate),
      scheduleLoadTime: this.getParsedTimeStr(shipment.scheduleLoadTime),
      originalLoadDate: this.getParsedDate(shipment.scheduleLoadDate),
      originalLoadTime: this.getParsedTimeStr(shipment.scheduleLoadTime),
      needByDate: this.getParsedDate(shipment.needByDate),
      scheduleInterchangeDate: this.getParsedDate(shipment.scheduleInterchangeDate)
    };
    this.form.patchValue(this.shipment);
  }
}
